import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDayTime } from 'lib/dateFormat';
import { cn } from 'lib/utils';
import { FEEDBACK_RATINGS, FEEDBACK_RATING_MAX, FEEDBACK_TAGS } from '../constants';
import type { TestFeedbackItem, TestFeedbackSummary } from '../types';

const CARD = 'rounded-xl border border-border bg-card p-4';
const CAPTION = 'text-[12.5px] font-medium text-muted-foreground';

const formatAverage = (value: number | null) => (value === null ? '—' : value.toFixed(1));

function AverageCard({ label, value }: { label: string; value: number | null }) {
  return (
    <div className={CARD}>
      <p className={CAPTION}>{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
        {formatAverage(value)}
        {value !== null && <span className="text-sm font-medium text-muted-foreground"> / {FEEDBACK_RATING_MAX}</span>}
      </p>
    </div>
  );
}

/** Five bars, 5 at the top. Widths are the share of the biggest bucket, so the tallest always fills the track. */
function RatingBars({ counts }: { counts: number[] }) {
  const { t } = useTranslation();
  const peak = Math.max(1, ...counts);
  return (
    <ul className="space-y-1.5" aria-label={t('feedback.q_enjoyment')}>
      {[...FEEDBACK_RATINGS].reverse().map((rating) => {
        const count = counts[rating - 1] ?? 0;
        return (
          <li key={rating} className="flex items-center gap-3 text-[13px] tabular-nums">
            <span className="w-3 text-muted-foreground">{rating}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted" aria-hidden>
              <span className="block h-full rounded-full bg-foreground" style={{ width: `${(count / peak) * 100}%` }} />
            </span>
            <span className="w-8 text-right text-foreground">{count}</span>
          </li>
        );
      })}
    </ul>
  );
}

function TagCounts({ counts }: { counts: TestFeedbackSummary['tagCounts'] }) {
  const { t } = useTranslation();
  const present = FEEDBACK_TAGS.filter((tag) => (counts[tag] ?? 0) > 0).sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0));
  if (present.length === 0) return null;
  return (
    <section className={CARD}>
      <h2 className="text-[14.5px] font-semibold text-foreground">{t('feedback.report_tags_title')}</h2>
      <ul className="mt-3 flex flex-wrap gap-2">
        {present.map((tag) => (
          <li
            key={tag}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[13px] text-foreground"
          >
            {t(`feedback.tag_${tag}`)}
            <span className="font-semibold tabular-nums text-muted-foreground">{counts[tag]}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Score({ label, value }: { label: string; value: number }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
      {label}
      <span
        aria-label={t('feedback.rating_label', { value })}
        className="rounded-md bg-muted px-1.5 py-0.5 font-semibold tabular-nums text-foreground"
      >
        {value}/{FEEDBACK_RATING_MAX}
      </span>
    </span>
  );
}

function FeedbackEntry({ item }: { item: TestFeedbackItem }) {
  const { t } = useTranslation();
  return (
    <li className="space-y-2 px-4 py-3.5">
      <p className={cn('text-sm font-semibold', item.name ? 'text-foreground' : 'text-muted-foreground')}>
        {item.name ?? t('feedback.report_item_unknown_name')}
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Score label={t('feedback.report_item_enjoyment')} value={item.enjoyment} />
        {item.examSimilarity !== undefined && (
          <Score label={t('feedback.report_item_similarity')} value={item.examSimilarity} />
        )}
        <time dateTime={item.submittedAt} className="ml-auto text-xs tabular-nums text-muted-foreground">
          {formatDayTime(item.submittedAt)}
        </time>
      </div>
      {item.tags.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {item.tags.map((tag) => (
            <li key={tag} className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-foreground/80">
              {t(`feedback.tag_${tag}`)}
            </li>
          ))}
        </ul>
      )}
      {/* Rendered as text only: a comment is whatever the learner typed. */}
      {item.comment && <p className="whitespace-pre-wrap break-words text-sm text-foreground">{item.comment}</p>}
    </li>
  );
}

export interface TestFeedbackReportViewProps {
  summary: TestFeedbackSummary;
  items: TestFeedbackItem[];
  /** Dims the entries while the next page loads. */
  isFetching?: boolean;
  footer?: React.ReactNode;
}

/** Presentational: the numbers first, then what each learner wrote. The page owns loading and paging. */
export function TestFeedbackReportView({ summary, items, isFetching = false, footer }: TestFeedbackReportViewProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className={CARD}>
          <p className={CAPTION}>{t('feedback.report_responses')}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{summary.total}</p>
        </div>
        <AverageCard label={t('feedback.q_enjoyment')} value={summary.averageEnjoyment} />
        <AverageCard label={t('feedback.q_similarity')} value={summary.averageExamSimilarity} />
      </div>

      <section className={CARD}>
        <h2 className="mb-3 text-[14.5px] font-semibold text-foreground">{t('feedback.q_enjoyment')}</h2>
        <RatingBars counts={summary.enjoymentCounts} />
      </section>

      <TagCounts counts={summary.tagCounts} />

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <h2 className="border-b border-border px-4 py-3 text-[14.5px] font-semibold text-foreground">
          {t('feedback.report_entries_title')}
        </h2>
        <ul className={cn('divide-y divide-border transition-opacity', isFetching && 'opacity-60')}>
          {items.map((item) => (
            <FeedbackEntry key={item.id} item={item} />
          ))}
        </ul>
        {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
      </section>
    </div>
  );
}

export default TestFeedbackReportView;
